import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAccountScope, getUser } from '../../../store/User';
import { Account, AddressInfo, OutsideInventorySchema } from '../../../types';
import FileDropArea from '../../Atoms/FileDropArea';
import { AccountSelectionForm } from '../UserCreation';
import { TextSpan } from '../../Atoms/Text';
import StatusDisplay from './StatusDisplay';
import { ConfirmationModal } from './ConfirmationModal';
import { datadogLogs } from '@datadog/browser-logs';
import { GridRowModel } from '@mui/x-data-grid';
import { createBulkOutsideInventory } from '../../../Api/OutsideInventory/outsideInventoryApi';
import { uploadFileAsS3Attachment } from '../../../Api/Admin/S3Attachments/s3AttachmentApi';
import {
  getOutsideInventorySchemaSubject,
  useOutsideInventorySchemasStore
} from '../../../store/OutsideInventorySchema';
import { calculateFileSHA, getFileMimeType } from './util';
import PotentialInventoryTable, { PotentialInventory } from './PotentialInventoryTable';
import { formatApiError, FormattedError } from './errorMessageFormatter';
import { createGeoCodedOutsideInventoryAddress, defaultOutsideInventoryForm } from '../../../store/OutsideInventory';
import { useInventory } from '../../../store/Inventory';
import { Icon } from '../../Atoms/Icon';
import { v4 } from 'uuid';
import {
  clearInvoiceParserJobs,
  createNewInvoiceParserJob,
  getInvoiceParserJobsSubject,
  handleStartNewInvoiceParserJob,
  InvoiceParserJob,
  updateInvoiceParserJob
} from '../../../store/InvoiceParser';
import { LineItem } from '../../../store/LineItem';
import {
  defaultSharedAccountCustomizationData,
  fetchSharedAccountCustomizations,
  getSharedAccountCustomizationsSubject,
  SharedAccountCustomizationData
} from '../../../store/SharedAccountCustomizations';

export const InvoiceParserForm = () => {
  const { accounts } = getUser();
  const navigate = useNavigate();
  const { fetchInventory } = useInventory();
  const { fetchOutsideInventorySchemas } = useOutsideInventorySchemasStore();
  const [selectedAccount, setSelectedAccount] = useState<Account | undefined>(
    getAccountScope()?.length === 1 ? getAccountScope()?.[0] : undefined
  );
  const [files, setFiles] = useState<File[]>([]);
  const [parserJobIds, setParserJobIds] = useState<string[]>([]);
  const [parserJobs, setParserJobs] = useState<InvoiceParserJob[]>([]);
  const [potentialInventory, setPotentialInventory] = useState<PotentialInventory[]>([]);
  const [selectedRows, setSelectedRows] = useState<GridRowModel[]>([]);
  const [isValidSelection, setIsValidSelection] = useState<boolean>(false);
  const [allSchemas, setAllSchemas] = useState<OutsideInventorySchema[]>([]);
  const [showResetModal, setShowResetModal] = useState<boolean>(false);
  const [showAddInventoryModal, setShowAddInventoryModal] = useState<boolean>(false);
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [formattedError, setFormattedError] = useState<FormattedError>({ title: '', message: '' });
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [sharedAccountCustomizations, setSharedAccountCustomizations] = useState<SharedAccountCustomizationData>(
    defaultSharedAccountCustomizationData(selectedAccount?.id, selectedAccount?.name)
  );

  // Fetch outside inventory schemas on load
  useEffect(() => {
    const schemasSub = getOutsideInventorySchemaSubject().subscribe((schemas) => setAllSchemas(schemas));
    const invoiceParserJobSub = getInvoiceParserJobsSubject().subscribe((jobs) => {
      setParserJobs(Object.values(jobs as Record<string, InvoiceParserJob>));
      setParserJobIds(Object.keys(jobs as Record<string, InvoiceParserJob>));
      setPotentialInventory(
        Object.values(jobs as Record<string, InvoiceParserJob>)
          .filter((job) => job.parseResult && job.inventoryList)
          .flatMap((job) => job.inventoryList || [])
          .sort((a, b) => (a.fileNumber || 0) - (b.fileNumber || 0))
      );
    });
    const sharedAccountCustSub = getSharedAccountCustomizationsSubject().subscribe((customizations) => {
      setSharedAccountCustomizations(
        customizations.find((cust) => cust.account_id === selectedAccount?.id) ||
          defaultSharedAccountCustomizationData(selectedAccount?.id, selectedAccount?.name)
      );
    });

    fetchOutsideInventorySchemas();
    fetchSharedAccountCustomizations(selectedAccount?.id ? [selectedAccount.id] : undefined);
    return () => {
      if (schemasSub) schemasSub.unsubscribe();
      if (invoiceParserJobSub) invoiceParserJobSub.unsubscribe();
      if (sharedAccountCustSub) sharedAccountCustSub.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (selectedAccount?.id) {
      fetchSharedAccountCustomizations([selectedAccount.id]);
    }
  }, [selectedAccount?.id]);

  useEffect(() => {
    //  If we have existing parser jobs but no files, set files from jobs
    if (parserJobIds.length > 0 && Object.keys(parserJobs).length > 0 && !files.length) {
      setFiles(parserJobs.filter((job) => job.file).map((job) => job.file) as File[]);
    }
  }, [parserJobIds, parserJobs, files]);

  // Function to find the active (latest version) schema for a product ID
  const findActiveSchemaByProductId = (productId: string): OutsideInventorySchema | undefined => {
    if (!productId) return undefined;

    return allSchemas
      .filter((schema) => schema.product_id === productId)
      .reduce(
        (latestVersion, schema): OutsideInventorySchema | undefined => {
          if (!latestVersion || schema.version > latestVersion.version) return schema;
          return latestVersion;
        },
        undefined as OutsideInventorySchema | undefined
      );
  };

  const handleSelectedRowsChange = (rows: GridRowModel[]) => {
    setSelectedRows(rows);
    setIsValidSelection(rows.length > 0);
  };

  const handleReset = () => {
    setShowResetModal(true);
  };

  const confirmReset = () => {
    setFiles([]);
    setSelectedRows([]);
    setIsValidSelection(false);
    setShowResetModal(false);
    clearInvoiceParserJobs();
    setPotentialInventory([]);
  };

  const handleAddToInventory = async () => {
    if (!selectedRows.length) return;
    setShowAddInventoryModal(true);
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    setSuccessMessage('');
    handleReset();
    navigate('/inventory');
  };

  const handleSuccessModalContinue = () => {
    setShowSuccessModal(false);
    setSuccessMessage('');
    handleReset();
  };

  const handleErrorModalClose = () => {
    setShowErrorModal(false);
    setFormattedError({ title: '', message: '' });
  };

  const safeNumberConversion = (value: unknown): number => {
    if (typeof value === 'number') {
      return value;
    }
    if (typeof value === 'string' && !isNaN(Number(value))) {
      return Number(value);
    }
    return 0;
  };

  const confirmAddToInventory = async () => {
    setShowAddInventoryModal(false);

    // Build tempId → source File map from all parser jobs so we can attach the invoice after creation
    const tempIdToFile = new Map<string, File>();
    parserJobs.forEach((job) => {
      if (job.file && job.inventoryList) {
        job.inventoryList.forEach((item) => {
          if (item.tempId) tempIdToFile.set(item.tempId, job.file as File);
        });
      }
    });

    try {
      //  Geocode selected rows that have addresses before creating inventory items, create map from row temp id to geocoding
      const geocodeResultsMap: Record<string, Omit<AddressInfo, 'number'>> = {};
      await Promise.all(
        selectedRows
          .filter((row) => row.type === 'outside_inventory' && row.address)
          .map(async (row) => {
            const geoCodedAddressInfo = await createGeoCodedOutsideInventoryAddress(row.address);
            if (geoCodedAddressInfo) {
              geocodeResultsMap[row.tempId as string] = {
                ...geoCodedAddressInfo
              };
            } else {
              datadogLogs.logger.warn(`Geocoding failed for address: ${row.address}`, row);
            }
          })
      );

      // Convert selected rows to inventory items
      const inventoryItems = await Promise.all(
        selectedRows
          .filter((row) => row.type === 'outside_inventory')
          .map(async (row) => {
            const defaultForm = defaultOutsideInventoryForm().formData;
            const activeSchema = findActiveSchemaByProductId(row.productId);
            if (row.productId && !activeSchema) {
              datadogLogs.logger.warn(`No active schema found for product ID: ${row.productId}`, row);
            }

            const geoCodedAddressInfo = row.address ? geocodeResultsMap[row.tempId as string] : null;

            return {
              ...defaultForm,
              ...row,
              id: '', // Ensure ID is clear for server generation
              mRC: safeNumberConversion(row.mRC),
              nRC: safeNumberConversion(row.nRC),
              licenseQuantity: String(row.licenseQuantity),
              uploadSpeedMbps: String(row.uploadSpeedMbps),
              downloadSpeedMbps: String(row.downloadSpeedMbps),
              speedValue: row.speedValue || '',
              speedUnit: row.speedUnit || '',
              speed: row.speedValue && row.speedUnit ? `${row.speedValue} ${row.speedUnit}` : row.speed || '',
              external_id: row.externalId || '',
              account_id: selectedAccount?.id || '',
              accountName: selectedAccount?.name || '',
              product_id: row.productId || '',
              supplier_id: row.supplierId || row.supplierName || '',
              outside_inventory_schema_id: activeSchema?.id || '',
              supplier: {
                id: row.supplierId || row.supplierName || '',
                name: row.supplierName || ''
              },
              product: {
                id: row.productId || '',
                name: row.productName || '',
                family: row.product?.family || row.productFamily || '',
                productCode: row.product?.productCode || ''
              },
              address: row.address || '',
              addressInfo: {
                ...defaultForm.addressInfo,
                locationName: row.address || '',
                number: row.locationCode || '',
                ...geoCodedAddressInfo
              },
              productSupportNumber: row.productSupportNumber || '',
              supplierSupportNumber: row.supplierSupportNumber || '',
              entry_method: 'invoice_parser',
              status: row.status || 'Active',
              line_items: [] as LineItem[],
              tempId: row.tempId || (row.id as string)
            };
          })
      );

      selectedRows
        .filter((row) => row.type === 'line_item')
        .forEach((row) => {
          const parentInventory = inventoryItems.find((item) => item.tempId === row.parentTempId);
          if (parentInventory) {
            const lineItem = {
              id: '', // Ensure ID is clear for server generation
              outside_inventory_id: parentInventory.id,
              external_line_item_id: row.name || '',
              description: row.name || '',
              mrc: safeNumberConversion(row.mRC),
              nrc: safeNumberConversion(row.nRC),
              product_id: row.productId || null
            };
            parentInventory.line_items.push(lineItem);
          }
        });

      // Create inventory items using bulk endpoint
      const result = await createBulkOutsideInventory(inventoryItems);

      if (result.data) {
        // Attach the source invoice to each created inventory item (non-blocking — errors are logged only)
        await Promise.allSettled(
          result.data.map((createdItem, index) => {
            const tempId = inventoryItems[index]?.tempId as string | undefined;
            const sourceFile = tempId ? tempIdToFile.get(tempId) : undefined;
            if (!sourceFile || !createdItem.id) return Promise.resolve();
            return uploadFileAsS3Attachment(sourceFile, createdItem.id, 'OutsideInventory').catch((err) => {
              datadogLogs.logger.warn('Failed to attach invoice to inventory item', { itemId: createdItem.id, err });
            });
          })
        );

        const successCount = result.data.length;
        setSuccessMessage(`Successfully created ${successCount} inventory item${successCount > 1 ? 's' : ''}.`);
        setShowSuccessModal(true);
        await fetchInventory(true);
      } else if (result.error) {
        datadogLogs.logger.error('Error creating inventory items', result.error);

        const formattedError = formatApiError(result.error);
        setFormattedError(formattedError);
        setShowErrorModal(true);
      }
    } catch (error) {
      setFormattedError({
        title: 'Error',
        message: 'An error occurred while creating inventory items. Please try again.'
      });
      setShowErrorModal(true);
      datadogLogs.logger.error('Error creating inventory items', error as Error);
    }
  };

  const handleFileDrop = async (droppedFiles: File[]) => {
    setFiles((prevFiles) => [...prevFiles, ...droppedFiles]);
    await Promise.all(
      droppedFiles.map(async (file) => {
        const newJobId = v4();
        createNewInvoiceParserJob(newJobId);
        const fileExtension = file.name.split('.').pop() || '';
        updateInvoiceParserJob(newJobId, {
          fileName: file.name,
          status: 'calculating_sha',
          selectedAccount,
          file: file
        });
        // Calculate SHA hash
        const shaHash = await calculateFileSHA(file);
        const docIdWithExtension = `${shaHash}.${fileExtension}`;
        const contentType = getFileMimeType(file);
        handleStartNewInvoiceParserJob(newJobId, docIdWithExtension, contentType, file);
      })
    );
  };

  return (
    <>
      <div className="flex flex-row justify-between my-4 px-4">
        <TextSpan>Let us add inventory for you. Just upload any client invoices — and you&apos;re done!</TextSpan>

        {parserJobs.length > 0 &&
          (parserJobs.every((job) => job.status === 'completed') ||
            parserJobs.some((job) => job.status === 'error')) && (
            <TextSpan className="flex items-center space-x-2">
              <Icon type="refreshBlue" />
              <button
                className="text-blue-500 text-sm"
                data-cy="clear-attachment"
                onClick={handleReset}>
                Clear File{parserJobs.length > 1 ? 's' : ''}
              </button>
            </TextSpan>
          )}
      </div>
      {parserJobs.length === 0 && files.length === 0 && (
        <>
          <div className="px-4 pb-7 pt-4">
            <AccountSelectionForm
              accounts={accounts}
              disabled={accounts?.length === 1}
              setSelectedAccount={(v) => setSelectedAccount(v)}
              selectedAccount={selectedAccount || null}
              styles="flex items-center w-52"
              showLabel={true}
              inputStyles="h-[1.75rem]"
            />
          </div>
        </>
      )}
      {files.length > 0 && parserJobs.length > 0 && (
        <div className="px-4 pb-6 pt-4">
          <StatusDisplay invoiceParserJobs={parserJobs} />
        </div>
      )}
      <div className="px-4 pb-1">
        <FileDropArea
          instructions="Drag and Drop an Invoice Here"
          subInstructions="Accepted file type: PDF; Maximum 10 files per upload"
          buttonText="Browse"
          allowedFileTypes={['application/pdf']}
          onFileDrop={handleFileDrop}
        />
      </div>
      {potentialInventory.length > 0 && (
        <div className="px-4">
          <PotentialInventoryTable
            inventoryItems={potentialInventory}
            onSelectedRowsChange={handleSelectedRowsChange}
            accounts={accounts}
            showMRCWithoutCharges={
              sharedAccountCustomizations.content.excludeInvoiceParserTaxes === true ||
              sharedAccountCustomizations.content.excludeInvoiceParserTaxes === undefined
            }
          />
        </div>
      )}
      {potentialInventory.length > 0 && (
        <div className="flex justify-end gap-2 px-4 py-4">
          <button
            onClick={handleReset}
            className="px-4 py-2 text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent">
            Cancel
          </button>
          <button
            onClick={handleAddToInventory}
            disabled={!isValidSelection || selectedRows.length === 0}
            className={`px-4 py-2 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 
              focus:border-transparent ${
                !isValidSelection || selectedRows.length === 0
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}>
            Add to Inventory List ({selectedRows.filter((r) => r.type === 'outside_inventory').length})
          </button>
        </div>
      )}

      {/* Account Mismatch Modal */}
      <ConfirmationModal
        open={parserJobs.some((job) => job.showAccountMismatchModal) || false}
        onClose={() =>
          parserJobIds.forEach((id) => {
            updateInvoiceParserJob(id as string, { showAccountMismatchModal: false });
          })
        }
        title="Account Mismatch"
        message="The account on some or all of the parsed inventory objects do not match the account selected."
        confirmText="Close"
        confirmColor="primary"
        confirmVariant="contained"
        showCancel={false}
      />

      {/* Add to Inventory Confirmation Modal */}
      <ConfirmationModal
        open={showAddInventoryModal}
        onClose={() => setShowAddInventoryModal(false)}
        onConfirm={confirmAddToInventory}
        title="Confirm Add to Inventory"
        message={`Are you sure you want to create ${selectedRows.filter((r) => r.type === 'outside_inventory').length} new outside inventory item${selectedRows.filter((r) => r.type === 'outside_inventory').length > 1 ? 's' : ''}?`}
        confirmText="Add to Inventory"
        cancelText="Cancel"
        confirmColor="primary"
        confirmVariant="contained"
        cancelColor="secondary"
        cancelVariant="outlined"
      />

      {/* Reset Confirmation Modal */}
      <ConfirmationModal
        open={showResetModal}
        onClose={() => setShowResetModal(false)}
        onConfirm={confirmReset}
        title="Confirm Reset"
        message="Are you sure you want to reset? This will clear all current results and allow you to upload a new file."
        confirmText="Reset"
        cancelText="Cancel"
        confirmColor="error"
        confirmVariant="contained"
        cancelColor="secondary"
        cancelVariant="outlined"
      />

      {/* Success Modal */}
      <ConfirmationModal
        open={showSuccessModal}
        onClose={handleSuccessModalContinue}
        onConfirm={handleSuccessModalClose}
        title="Success"
        message={successMessage}
        confirmText="Go to Inventory"
        confirmColor="primary"
        confirmVariant="contained"
        cancelColor="primary"
        cancelText="Continue Adding"
      />

      {/* Error Modal */}
      <ConfirmationModal
        open={showErrorModal}
        onClose={handleErrorModalClose}
        title={formattedError.title}
        message={formattedError.message}
        details={formattedError.details}
        confirmText="OK"
        confirmColor="error"
        confirmVariant="contained"
        showCancel={false}
      />
    </>
  );
};
